import { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import type { Country, Subdivision } from 'api/referentialsApi';
import { useCreateSubdivision, useUpdateSubdivision } from 'hooks/useReferentials';
import { subdivisionLabel } from 'utils/subdivisionLabel';
import { apiErrorMessage } from 'utils/errorMessage';
import { useToast } from 'components/ui/toast';

export interface SubdivisionFormDialogProps {
  open: boolean;
  /** null = creation. */
  subdivision: Subdivision | null;
  countries: Country[];
  onClose: () => void;
}

interface State {
  countryCode: string;
  code: string;
  name: string;
  error: string | null;
}

type Action =
  | { type: 'reset'; subdivision: Subdivision | null; fallbackCountry: string }
  | { type: 'set'; field: 'countryCode' | 'code' | 'name'; value: string }
  | { type: 'setError'; error: string | null };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'reset':
      return {
        countryCode: action.subdivision?.country_code ?? action.fallbackCountry,
        code: action.subdivision?.code ?? '',
        name: action.subdivision?.name ?? '',
        error: null,
      };
    case 'set':
      return { ...state, [action.field]: action.value, error: null };
    case 'setError':
      return { ...state, error: action.error };
    default:
      return state;
  }
};

/**
 * Create or rename a subdivision. On an existing row the country and the code are shown but not
 * editable: the code is the stable slug, and reparenting a subdivision would silently move every
 * technician already scoped to it.
 */
const SubdivisionFormDialog = ({ open, subdivision, countries, onClose }: SubdivisionFormDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const activeCountries = countries.filter((country) => country.is_active);
  const [state, dispatch] = useReducer(reducer, {
    countryCode: activeCountries[0]?.code ?? '',
    code: '',
    name: '',
    error: null,
  });
  const createSubdivision = useCreateSubdivision();
  const updateSubdivision = useUpdateSubdivision();

  // Reset on open or when another row is targeted — never on a refetch, which would wipe the
  // fields being typed.
  const subdivisionId = subdivision?.id ?? null;
  const fallbackCountry = activeCountries[0]?.code ?? '';
  useEffect(() => {
    if (open) dispatch({ type: 'reset', subdivision, fallbackCountry });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subdivisionId, fallbackCountry]);

  const saving = createSubdivision.isPending || updateSubdivision.isPending;
  const selectedCountry = countries.find((country) => country.code === state.countryCode) ?? null;

  const submit = async () => {
    const name = state.name.trim();
    const code = state.code.trim();
    if (!name || (!subdivision && !code)) {
      dispatch({ type: 'setError', error: t('A code and a name are required') });
      return;
    }
    try {
      if (subdivision) {
        await updateSubdivision.mutateAsync({ id: subdivision.id, data: { name } });
      } else {
        await createSubdivision.mutateAsync({ countryCode: state.countryCode, code, name });
      }
      toast.success(subdivision ? t('{{name}} saved', { name }) : t('{{name}} created', { name }));
      onClose();
    } catch (error) {
      // Stays open on failure: closing would lose the input and hide the reason (a taken code).
      dispatch({ type: 'setError', error: apiErrorMessage(t, error) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {subdivision
              ? t('Edit {{subdivision}}', { subdivision: subdivisionLabel(t, subdivision.country_subdivision_type) })
              : t('New {{subdivision}}', {
                  subdivision: subdivisionLabel(t, selectedCountry?.subdivision_type ?? ''),
                })}
          </DialogTitle>
          <DialogDescription>
            {t('The code is set once and never changes; only the label can be corrected.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="subdivision-country">{t('Country')}</Label>
            {subdivision ? (
              <p id="subdivision-country" className="text-sm text-gray-600 dark:text-gray-300">
                {subdivision.country_name}
              </p>
            ) : (
              <Select
                value={state.countryCode}
                onValueChange={(value: string) => dispatch({ type: 'set', field: 'countryCode', value })}
              >
                <SelectTrigger id="subdivision-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activeCountries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subdivision-code">{t('Code')}</Label>
            {subdivision ? (
              <p id="subdivision-code" className="font-mono text-sm text-gray-600 dark:text-gray-300">
                {subdivision.code}
              </p>
            ) : (
              <Input
                id="subdivision-code"
                value={state.code}
                placeholder="75"
                onChange={(event) => dispatch({ type: 'set', field: 'code', value: event.target.value })}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subdivision-name">{t('Name')}</Label>
            <Input
              id="subdivision-name"
              value={state.name}
              onChange={(event) => dispatch({ type: 'set', field: 'name', value: event.target.value })}
            />
          </div>

          {state.error && <p className="text-sm text-red-600">{state.error}</p>}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('Cancel')}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? t('Saving…') : t('Save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubdivisionFormDialog;
